import { Box, Button, Flex, Image, Text } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router';

import type { MergedPricingService } from 'screens/Prices/utils/mergePricingService';
import { AlmaMention } from './AlmaMention';
import { trackSelectItem } from 'utils/tracking';
import { getBrandColor } from 'utils/brandColor';

const cardBorderRadius = `${Number(mondayMindbody?.customization?.cardBorderRadius) || 12}px`;
const brandColor = getBrandColor();

interface PackCardProps {
  /** Pre-merged service (admin config + live Mindbody data). */
  merged: MergedPricingService;
  /** Mention Alma des cartes (pricingConfig.globalTexts.almaCardText). */
  almaText: string;
}

/**
 * Visual card for a single pack/service — same layout as TopFeatureCard
 * maquette mode: gray bg, left-aligned, optional ribbon bar, bottom row
 * with CTA (left) + validity/limit (right).
 */
export function PackCard({ merged, almaText }: PackCardProps) {
  const isCustomCard = !merged.service;
  const navigate = useNavigate();
  const location = useLocation();
  const onCheckout = merged.service
    ? () => {
        const product = { ...merged.service!, type: 'service' as const, IsIntroOffer: !!merged.service!.IsIntroOffer };
        trackSelectItem(product as unknown as Record<string, unknown>);
        navigate(`/checkout/id/${product.Id}`, { state: { product, from: location } });
      }
    : undefined;

  const accentColor = merged.accentColor || merged.ribbon?.color || brandColor;
  const hasBottomInfo = !!(merged.validity || merged.limitText || merged.almaEnabled);
  const hasBottomRow = !!(hasBottomInfo || (merged.ctaLabel && onCheckout));
  const hasContent = !!(merged.title || merged.priceLabel || hasBottomRow);

  const hasImage = !!(merged.image && merged.image.url);
  // Text color: admin override > white over image > dark default
  const textColor = merged.textColor || (hasImage ? 'white' : '#1a1a1a');
  const subTextColor = merged.textColor || (hasImage ? 'whiteAlpha.800' : '#4a4a4a');
  const cardBg = merged.bgColor || '#f5f5f5';

  return (
    <Box
      className={`serviceCard${isCustomCard ? ' serviceCard--custom' : ''}${hasImage ? ' serviceCard--hasImage' : ''}`}
      borderRadius={cardBorderRadius}
      overflow="hidden"
      bg={cardBg}
      display="flex"
      flexDirection="column"
      minH="280px"
      position="relative"
      cursor={onCheckout ? 'pointer' : undefined}
      onClick={onCheckout}
    >
      {/* ── Full-bleed background image ── */}
      {hasImage && (
        <Image
          className="serviceCardImage"
          src={merged.image!.url}
          alt={merged.title || ''}
          position="absolute"
          inset={0}
          w="full"
          h="full"
          objectFit="cover"
          zIndex={0}
        />
      )}

      {/* ── Ribbon bar — reserve space even when absent so titles align ── */}
      <Box
        className={merged.ribbon ? 'serviceRibbon' : 'serviceRibbonSpacer'}
        w="full"
        px={5}
        py={merged.ribbon ? 2.5 : 0}
        minH="40px"
        bg={merged.ribbon ? (merged.ribbon.color || accentColor) : 'transparent'}
        color="white"
        fontSize="sm"
        fontWeight="700"
        textTransform="uppercase"
        letterSpacing="wide"
        flexShrink={0}
        display="flex"
        alignItems="center"
        position="relative"
        zIndex={1}
      >
        {merged.ribbon?.text}
      </Box>

      {/* ── Text content — overlaid on top of image when present ── */}
      {hasContent && (
        <Flex className="serviceCardBody" direction="column" flex="1" px={5} pt={4} pb={4} position="relative" zIndex={1}>
          {merged.title && (
            <Text className="serviceName" fontSize={{ base: '18px', md: '20px' }} fontWeight="900" textTransform="uppercase" lineHeight="1.1" color={textColor}>
              {merged.title}
            </Text>
          )}

          {merged.priceLabel && !merged.hidePrice && (
            <Box className="servicePriceBlock" mt={3}>
              {merged.pricePerSessionPrice ? (
                <>
                  <Text className="servicePricePerSession" fontSize={{ base: '22px', md: '26px' }} fontWeight="400" textTransform="uppercase" color={textColor} lineHeight="1">
                    <Text as="span" fontWeight="900">{merged.pricePerSessionPrice}</Text>{merged.pricePerSessionSuffix}
                  </Text>
                  {merged.priceSubLabel && (
                    <Text className="servicePriceSubLabel" fontSize="13px" fontWeight="400" textTransform="uppercase" color={textColor} mt={4}>
                      {merged.priceSubLabel}
                    </Text>
                  )}
                </>
              ) : (
                <Text className="servicePrice" fontSize={{ base: '28px', md: '32px' }} fontWeight="900" color={textColor} lineHeight="1">
                  {merged.priceLabel}
                </Text>
              )}
            </Box>
          )}

          <Box flex="1" minH={3} />

          {/* ── Bottom row: CTA left + info right ── */}
          {hasBottomRow && (
            <Flex className="serviceCardBottomRow" align="flex-end" justify="space-between" gap={3}>
              {merged.ctaLabel && onCheckout ? (
                <Button
                  className="serviceCardButton"
                  borderRadius="full"
                  onClick={(e) => { e.stopPropagation(); onCheckout(); }}
                  bg={accentColor}
                  color="white"
                  _hover={{ opacity: 0.9 }}
                  px={6}
                  h="38px"
                  fontSize="13px"
                  fontWeight="700"
                  textTransform="uppercase"
                  letterSpacing="wide"
                  flexShrink={0}
                >
                  {merged.ctaLabel}
                </Button>
              ) : (
                <Box />
              )}

              {(hasBottomInfo || merged.almaEnabled) && (
                <Box className="serviceCardBottomInfo" textAlign="right">
                  {merged.validity && (
                    <Text className="serviceExpiration" fontSize="12px" color={subTextColor} lineHeight="1.4">
                      {merged.validity}
                    </Text>
                  )}
                  {merged.almaEnabled && <AlmaMention variant="card" text={almaText} color={subTextColor} fontSize="12px" />}
                  {merged.limitText && (
                    <Text className="serviceLimitText" fontSize="11px" color={subTextColor} lineHeight="1.4">
                      {merged.limitText}
                    </Text>
                  )}
                </Box>
              )}
            </Flex>
          )}
        </Flex>
      )}
    </Box>
  );
}
